import React, { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Grid, Skeleton, Tabs, Tab, Box, IconButton, Typography, Dialog, DialogTitle, DialogContent, Alert, Chip } from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { lightBlue, grey, yellow, lightGreen } from "@mui/material/colors"
import { AutocompleteSeeker } from "@ui-seeker/core"
import PageWrapper from "Pages/@PageWrapper"
import { AppCrudTable } from "@components"
import { AUTO_EUROPE_TOOL_ROUTES } from "@constants/routes"
import { autoEuropeNotificationsListData, autoEuropeNotificationsIsLoading, autoEuropeCheckersData, autoEuropeCheckersIsLoading } from "Pages/AutoEuropeTool/Notifications/@signals"
import useAutoEuropeNotificationsTable from "Pages/AutoEuropeTool/Notifications/@hooks/useTable"

const c = v => <Box sx={{ textAlign: 'center' }}>{v ?? ''}</Box>
const pct = v => v != null ? `${v}%` : ''

// Query-param values arrive as strings; numeric ids must match the (numeric) option values.
const coerceParam = v => v != null && /^\d+$/.test(v) ? Number(v) : v

// Accepts an int yyyymmdd or a string YYYY-MM-DD and renders MM/DD/YYYY.
const formatDate = v => {
	if (!v) return ''
	const s = String(v).replace(/-/g, '')
	if (s.length !== 8) return v
	return `${s.slice(4, 6)}/${s.slice(6, 8)}/${s.slice(0, 4)}`
}

// Same input, rendered DD/MM/YYYY (used by the Pickup column).
const formatDateDMY = v => {
	if (!v) return ''
	const s = String(v).replace(/-/g, '')
	if (s.length !== 8) return v
	return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`
}

// Type-to-search filter. Matches (case-insensitive, substring) against the
// displayed value (fmt) and the raw value, so either representation works.
const makeTextFilter = (fmt = v => v) => ({
	filterType: 'textField',
	filterOptions: {
		logic: (value, filters) => {
			if (!filters?.[0]) return false
			const q = String(filters[0]).toLowerCase()
			const hit = String(fmt(value) ?? '').toLowerCase().includes(q)
				|| String(value ?? '').toLowerCase().includes(q)
			return !hit
		},
	},
})

const textFilter = makeTextFilter()

const formatUndercut = row => {
	if (row.configUndercutValue == null) return ''
	const arrow = row.configUndercutDirection === 0 ? '⬇' : '⬆'
	const unit = row.configUndercutType === 1 ? '%' : '$'
	return `${arrow}${row.configUndercutValue}${unit}`
}

// Fields moved out of the table into the Supplier "i" modal.
const supplierInfoFields = row => [
	['Conf Min Margin', row.configMinMargin != null ? `${row.configMinMargin}%` : ''],
	['Conf Min Profit', row.configMinProfit ?? ''],
	['Conf Undercut', formatUndercut(row)],
	['Conf Target Pos', row.configTargetPosition || '-'],
	['supplierName', row.autoeuropeProvider ?? ''],
]

// Formula + the row values it uses, for each computed column (mirrors backend _compute_row).
const FORMULA_INFO = {
	newNet: row => ({
		formula: 'New Net = Net × (1 + Conf Min Margin / 100)',
		values: [['Net', row.mainNetPrice], ['Conf Min Margin', pct(row.configMinMargin)]],
	}),
	retail: row => ({
		formula: row.configUndercutType === 1
			? 'Retail = Site Price × (1 ± Undercut / 100)'
			: 'Retail = Site Price ± Undercut',
		values: [['Site Price', row.competitorPrice], ['Undercut', formatUndercut(row)]],
	}),
	profit: row => ({
		formula: 'Profit = Retail − Net',
		values: [['Retail', row.retail], ['Net', row.mainNetPrice]],
	}),
	margin: row => ({
		formula: 'Margin = (1 − Net / Retail) × 100',
		values: [['Net', row.mainNetPrice], ['Retail', row.retail]],
	}),
	// Conf Min Margin is a markup over Net, so it is compared against the Retail markup, not the Margin column.
	newRetail: row => ({
		formula: 'Markup = (Retail / Net − 1) × 100\nNew Retail = max(Net + Conf Min Profit, New Net if Markup < Conf Min Margin else Retail)',
		values: [
			['Net', row.mainNetPrice],
			['Conf Min Profit', row.configMinProfit],
			['Markup', row.retail && row.mainNetPrice ? pct(+((row.retail / row.mainNetPrice - 1) * 100).toFixed(1)) : ''],
			['Conf Min Margin', pct(row.configMinMargin)],
			['New Net', row.newNetPrice],
			['Retail', row.retail],
		],
	}),
	newProfit: row => ({
		formula: 'New Pft = New Retail − Net',
		values: [['New Retail', row.newRetail], ['Net', row.mainNetPrice]],
	}),
	newProfitDaily: row => ({
		formula: 'New Pft/Day = New Pft / Lor',
		values: [['New Pft', row.newProfit], ['Lor', row.lengthOfRental]],
	}),
	newMargin: row => ({
		formula: 'New Margin = (New Pft / Net) × 100',
		values: [['New Pft', row.newProfit], ['Net', row.mainNetPrice]],
	}),
	rev: row => row.configInsuranceDelta > 0
		? {
			formula: 'REV = max(((Final Retail excl. INS − New Net) / New Net) × 100, 0)',
			values: [['Final Retail excl. INS', row.finalRetailExclIns], ['New Net', row.newNetPrice]],
		}
		: {
			formula: 'REV = max(((New Retail − New Net) / New Net) × 100, 0)',
			values: [['New Retail', row.newRetail], ['New Net', row.newNetPrice]],
		},
	retailExclIns: row => ({
		formula: 'Retail excl. INS = New Retail − INS Delta × Lor',
		values: [['New Retail', row.newRetail], ['INS Delta', row.configInsuranceDelta], ['Lor', row.lengthOfRental]],
	}),
	minMaxRecalc: row => {
		const lor = row.lengthOfRental
		const ppd = lor ? (row.newRetail - row.newNetPrice) / lor : null
		return {
			formula: 'Profit/Day = (New Retail − New Net) / Lor\nif Profit/Day < INS Min/Day → MinMaxRecalc = New Net + INS Min/Day × Lor\nif Profit/Day > INS Max/Day → MinMaxRecalc = New Net + INS Max/Day × Lor\nelse → MinMaxRecalc = New Retail',
			values: [
				['New Retail', row.newRetail],
				['New Net', row.newNetPrice],
				['Lor', lor],
				['Profit/Day', ppd != null ? +ppd.toFixed(4) : ''],
				['INS Min/Day', row.configInsuranceMinPd],
				['INS Max/Day', row.configInsuranceMaxPd],
			],
		}
	},
	finalRetailExclIns: row => ({
		formula: 'Final Retail excl. INS = MinMaxRecalc − INS Delta × Lor',
		values: [['MinMaxRecalc', row.minMaxRecalc], ['INS Delta', row.configInsuranceDelta], ['Lor', row.lengthOfRental]],
	}),
}

// Column-group background colors.
const BLUE = lightBlue[50]      // Pickup..Site Margin
const GREY = grey[200]          // Retail..REV
const WHITE = '#fff'            // New Net
const YELLOW = yellow[100]      // Data: acriss
const INS = lightGreen[100]     // Insurance computed columns

// Sticky helpers for the leading columns (Pickup..Net), unaffected by horizontal scroll.
const freezeHead = left => () => ({ sx: { position: 'sticky', top: 0, left, zIndex: '150 !important' } })
const freezeBody = (left, width, bg) => () => ({
	sx: {
		position: 'sticky',
		left,
		zIndex: 3,
		minWidth: width,
		maxWidth: width,
		backgroundColor: bg,
	}
})
const cellBg = bg => () => ({ sx: { backgroundColor: bg } })

const buildXlsxColumns = (onInfo, hasInsurance) => {
	// Cell with an "i" button that opens the column formula + this row's values in the modal.
	const formulaCell = (title, build, fmt = v => v ?? '') => (v, row) => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .5 }}>
		<span>{fmt(v)}</span>
		<IconButton size="small" sx={{ p: 0 }} onClick={() => { const i = build(row); onInfo({ title, formula: i.formula, fields: i.values, result: fmt(v) }) }}>
			<InfoOutlinedIcon fontSize="small" color="primary"/>
		</IconButton>
	</Box>

	return [
	{ name: 'pickup_date_translation', label: 'Pickup', options: { customBodyRowRender: v => c(formatDateDMY(v)), setCellHeaderProps: freezeHead(0), setCellProps: freezeBody(0, 95, BLUE) } },
	{ name: 'lengthOfRental', label: 'Lor', options: { customBodyRowRender: v => c(v), setCellHeaderProps: freezeHead(95), setCellProps: freezeBody(95, 55, BLUE) } },
	{ name: 'acrissCode', label: 'SIPP', options: { customBodyRowRender: (v, row) => c(row.originalCategory || v), setCellHeaderProps: freezeHead(150), setCellProps: freezeBody(150, 60, BLUE) } },
	{
		name: 'main_id_master_provider_translation',
		label: 'Supplier',
		options: {
			customBodyRowRender: (v, row) => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .5 }}>
				<span>{v ?? ''}</span>
				<IconButton size="small" sx={{ p: 0 }} onClick={() => onInfo({ title: 'Supplier config', fields: supplierInfoFields(row) })}>
					<InfoOutlinedIcon fontSize="small" color="primary"/>
				</IconButton>
			</Box>,
			setCellHeaderProps: freezeHead(210),
			setCellProps: freezeBody(210, 150, BLUE),
		}
	},
	{ name: 'mainNetPrice', label: 'Net', options: { ...textFilter, customBodyRowRender: v => c(v), setCellHeaderProps: freezeHead(360), setCellProps: freezeBody(360, 80, BLUE) } },
	{ name: 'mainPrice', label: 'AE Retail', options: { ...textFilter, customBodyRowRender: v => c(v), setCellProps: cellBg(BLUE) } },
	{ name: 'mainMargin', label: 'AE Margin', options: { ...textFilter, customBodyRowRender: v => c(v != null && v !== '' ? `${Math.abs(v)}%` : ''), setCellProps: cellBg(BLUE) } },
	{ name: 'competitor_id_site_translation', label: 'Site', options: { display: false, customBodyRowRender: v => c(v), setCellProps: cellBg(BLUE) } },
	{ name: 'competitor_id_master_web_supplier_translation', label: 'Web Supplier', options: { display: false, customBodyRowRender: v => c(v), setCellProps: cellBg(BLUE) } },
	{
		name: 'competitorPrice',
		label: 'Site Price',
		options: {
			...textFilter,
			customBodyRowRender: (v, row) => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .5 }}>
				<span>{v ?? ''}</span>
				<IconButton size="small" sx={{ p: 0 }} onClick={() => onInfo({ title: 'Site', fields: [
						['Site', row.competitor_id_site_translation ?? ''],
						...(row.competitor_id_master_web_supplier_translation ? [['Web Supplier', row.competitor_id_master_web_supplier_translation]] : []),
						['Competitor Pos', row.competitorPosition || '-'],
						['Conf Target Pos', row.configTargetPosition || '-'],
					] })}>
					<InfoOutlinedIcon fontSize="small" color="primary"/>
				</IconButton>
			</Box>,
			setCellProps: cellBg(BLUE),
		}
	},
	{ name: 'competitorMargin', label: 'Site Margin', options: { ...textFilter, customBodyRowRender: v => c(v != null && v !== '' ? `${-1 * v}%` : ''), setCellProps: cellBg(BLUE) } },
	{ name: 'competitorPosition', label: 'Competitor Pos', options: { ...textFilter, display: false, customBodyRowRender: v => c(v || '-'), setCellProps: cellBg(BLUE) } },
	{ name: 'configTargetPosition', label: 'Conf Target Pos', options: { ...textFilter, display: false, customBodyRowRender: v => c(v || '-'), setCellProps: cellBg(BLUE) } },
	{ name: 'newNetPrice', label: 'New Net', options: { ...textFilter, customBodyRowRender: formulaCell('New Net', FORMULA_INFO.newNet), setCellProps: cellBg(WHITE) } },
	{ name: 'retail', label: 'Retail', options: { ...textFilter, customBodyRowRender: formulaCell('Retail', FORMULA_INFO.retail), setCellProps: cellBg(GREY) } },
	{ name: 'profit', label: 'Profit', options: { ...textFilter, customBodyRowRender: formulaCell('Profit', FORMULA_INFO.profit), setCellProps: cellBg(GREY) } },
	{ name: 'margin', label: 'Margin', options: { ...makeTextFilter(pct), customBodyRowRender: formulaCell('Margin', FORMULA_INFO.margin, pct), setCellProps: cellBg(GREY) } },
	{ name: 'newRetail', label: 'New Retail', options: { ...textFilter, customBodyRowRender: formulaCell('New Retail', FORMULA_INFO.newRetail), setCellProps: cellBg(GREY) } },
	{ name: 'newProfit', label: 'New Pft', options: { ...textFilter, customBodyRowRender: formulaCell('New Pft', FORMULA_INFO.newProfit), setCellProps: cellBg(GREY) } },
	{ name: 'newProfitDaily', label: 'New Pft/Day', options: { ...textFilter, customBodyRowRender: formulaCell('New Pft/Day', FORMULA_INFO.newProfitDaily), setCellProps: cellBg(GREY) } },
	{ name: 'newMargin', label: 'New Margin', options: { ...makeTextFilter(pct), customBodyRowRender: formulaCell('New Margin', FORMULA_INFO.newMargin, pct), setCellProps: cellBg(GREY) } },
	{ name: 'rev', label: 'REV', options: { customBodyRowRender: formulaCell('REV', FORMULA_INFO.rev, pct), setCellProps: cellBg(GREY) } },
	...(hasInsurance ? [
		{ name: 'retailExclIns', label: 'Retail excl. INS', options: { ...textFilter, customBodyRowRender: formulaCell('Retail excl. INS', FORMULA_INFO.retailExclIns), setCellProps: cellBg(INS) } },
		{ name: 'minMaxRecalc', label: 'MinMaxRecalc', options: { ...textFilter, customBodyRowRender: formulaCell('MinMaxRecalc', FORMULA_INFO.minMaxRecalc), setCellProps: cellBg(INS) } },
		{ name: 'finalRetailExclIns', label: 'Final Retail excl. INS', options: { ...textFilter, customBodyRowRender: formulaCell('Final Retail excl. INS', FORMULA_INFO.finalRetailExclIns), setCellProps: cellBg(INS) } },
		{ name: 'configInsuranceDelta', label: 'INS Delta', options: { ...textFilter, customBodyRowRender: v => c(v), setCellProps: cellBg(INS) } },
		{ name: 'configInsuranceMinPd', label: 'INS Min/Day', options: { ...textFilter, customBodyRowRender: v => c(v), setCellProps: cellBg(INS) } },
		{ name: 'configInsuranceMaxPd', label: 'INS Max/Day', options: { ...textFilter, customBodyRowRender: v => c(v), setCellProps: cellBg(INS) } },
	] : []),
	]
}

const buildInsuranceColumns = onInfo => {
	const formulaCell = (title, build, fmt = v => v ?? '') => (v, row) => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .5 }}>
		<span>{fmt(v)}</span>
		<IconButton size="small" sx={{ p: 0 }} onClick={() => { const i = build(row); onInfo({ title, formula: i.formula, fields: i.values, result: fmt(v) }) }}>
			<InfoOutlinedIcon fontSize="small" color="primary"/>
		</IconButton>
	</Box>

	return [
		{ name: 'pickup_date_translation', label: 'Pickup', options: { customBodyRowRender: v => c(formatDateDMY(v)), setCellProps: cellBg(BLUE) } },
		{ name: 'lengthOfRental', label: 'Lor', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(BLUE) } },
		{ name: 'acrissCode', label: 'SIPP', options: { customBodyRowRender: (v, row) => c(row.originalCategory || v), setCellProps: cellBg(BLUE) } },
		{ name: 'main_id_master_provider_translation', label: 'Supplier', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(BLUE) } },
		{ name: 'mainNetPrice', label: 'Net', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(BLUE) } },
		{ name: 'newNetPrice', label: 'New Net', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(WHITE) } },
		{ name: 'newRetail', label: 'New Retail', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(GREY) } },
		{ name: 'retailExclIns', label: 'Retail excl. INS', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(INS) } },
		{ name: 'minMaxRecalc', label: 'MinMaxRecalc', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(INS) } },
		{ name: 'finalRetailExclIns', label: 'Final Retail excl. INS', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(INS) } },
		{ name: 'rev', label: 'REV', options: { customBodyRowRender: formulaCell('REV', FORMULA_INFO.rev, pct), setCellProps: cellBg(GREY) } },
	]
}

const CSV_COLUMNS = [
	{ name: 'pricingId', label: 'pricingId', options: { customBodyRowRender: v => c(v) } },
	{ name: 'agencyId', label: 'agencyId', options: { customBodyRowRender: v => c(v) } },
	{ name: 'agentId', label: 'agentId', options: { customBodyRowRender: v => c(v) } },
	{ name: 'bookingDateStart', label: 'bookingStart', options: { customBodyRowRender: v => c(formatDate(v)) } },
	{ name: 'bookingDateEnd', label: 'bookingEnd', options: { customBodyRowRender: v => c(formatDate(v)) } },
	{ name: 'pickupDateStart', label: 'pickupStart', options: { customBodyRowRender: v => c(formatDate(v)) } },
	{ name: 'pickupDateEnd', label: 'pickupEnd', options: { customBodyRowRender: v => c(formatDate(v)) } },
	{ name: 'officeCountry', label: 'countryCode', options: { customBodyRowRender: v => c(v) } },
	{ name: 'autoeuropeProvider', label: 'supplierName', options: { customBodyRowRender: v => c(v) } },
	{ name: 'acrissCode', label: 'acriss', options: { customBodyRowRender: v => c(v), setCellProps: cellBg(YELLOW) } },
	{ name: 'rev', label: 'percent', options: { customBodyRowRender: v => c(v != null ? `${v}%` : '') } },
	{ name: 'lengthOfRentalStart', label: 'durationRangeFrom', options: { customBodyRowRender: v => c(v) } },
	{ name: 'lengthOfRentalEnd', label: 'durationRangeTo', options: { customBodyRowRender: v => c(v) } },
	{ name: 'daysToPickup', label: 'daysToPickup', options: { customBodyRowRender: v => c(v) } },
	{ name: 'hoursToPickup', label: 'hoursToPickup', options: { customBodyRowRender: v => c(v) } },
]

// Most frequent value in a list (majority). Ties resolve to the first seen.
const mode = values => {
	const count = new Map()
	for (const v of values) count.set(v, (count.get(v) || 0) + 1)
	let best, bestN = -1
	for (const [v, n] of count) if (n > bestN) { bestN = n; best = v }
	return best
}

// Cell that turns red when its config value differs from the majority provider.
const confCell = (v, isDiff) => <Box sx={{ textAlign: 'center', ...(isDiff && { color: 'error.main', fontWeight: 'bold' }) }}>{v ?? ''}</Box>

// One row per supplier + distinct config, showing the same config the Supplier "i" modal shows.
// Config columns are highlighted in red when a provider deviates from the rest.
const buildProviderColumns = rows => {
	const marginMode = mode(rows.map(r => r.confMinMargin))
	const profitMode = mode(rows.map(r => r.confMinProfit))
	const undercutMode = mode(rows.map(r => r.confUndercut))
	return [
		{ name: 'supplier', label: 'Supplier', options: { customBodyRowRender: v => c(v) } },
		{ name: 'lor', label: 'Lor', options: { customBodyRowRender: v => c(v) } },
		{ name: 'confMinMargin', label: 'Conf Min Margin', options: { customBodyRowRender: v => confCell(v, v !== marginMode) } },
		{ name: 'confMinProfit', label: 'Conf Min Profit', options: { customBodyRowRender: v => confCell(v, v !== profitMode) } },
		{ name: 'confUndercut', label: 'Conf Undercut', options: { customBodyRowRender: v => confCell(v, v !== undercutMode) } },
		{ name: 'supplierName', label: 'supplierName', options: { customBodyRowRender: v => c(v) } },
	]
}

// Compact label for the set of LoR (length of rental) values a config applied to.
const lorLabel = lors => {
	const nums = [...lors].sort((a, b) => a - b)
	if (!nums.length) return '*'
	const min = nums[0], max = nums[nums.length - 1]
	return min === max ? String(min) : `${min} → ${max}`
}

// One row per supplier + distinct config. Config now varies by LoR (duration
// exceptions), so a provider can yield several rows; the Lor column shows the
// duration range each config applied to (derived from the inserted result rows).
// Providers with a single config across all durations show '*'.
const buildProviderRows = data => {
	const providers = new Map()
	for (const row of data) {
		const supplier = row.main_id_master_provider_translation ?? ''
		const key = supplier || row.autoeuropeProvider
		if (key == null) continue
		const configKey = [row.configMinMargin, row.configMinProfit, row.configUndercutValue, row.configUndercutType, row.configUndercutDirection].join('|')
		if (!providers.has(key)) providers.set(key, new Map())
		const configs = providers.get(key)
		if (!configs.has(configKey)) {
			configs.set(configKey, {
				supplier,
				confMinMargin: row.configMinMargin != null ? `${row.configMinMargin}%` : '',
				confMinProfit: row.configMinProfit ?? '',
				confUndercut: formatUndercut(row),
				supplierName: row.autoeuropeProvider ?? '',
				lors: new Set(),
			})
		}
		if (row.lengthOfRental != null) configs.get(configKey).lors.add(Number(row.lengthOfRental))
	}

	const rows = []
	for (const configs of providers.values()) {
		const hasVariation = configs.size > 1
		for (const { lors, ...entry } of configs.values()) {
			rows.push({ ...entry, lor: hasVariation ? lorLabel(lors) : '*' })
		}
	}
	return rows.sort((a, b) => String(a.supplier).localeCompare(String(b.supplier)) || String(a.lor).localeCompare(String(b.lor)))
}

const buildInsuranceConfigRows = data => {
	const insRows = data.filter(r => r.configInsuranceDelta > 0)
	if (!insRows.length) return []
	// El delta ya no es único: se puede sobreescribir por rango de duración, así que
	// entra en la clave del grupo junto al min/max en vez de tomarse de la primera fila.
	const groups = new Map()
	for (const row of insRows) {
		const key = `${row.configInsuranceDelta}|${row.configInsuranceMinPd}|${row.configInsuranceMaxPd}`
		if (!groups.has(key)) {
			groups.set(key, {
				deltaEurPd: row.configInsuranceDelta,
				minPd: row.configInsuranceMinPd > 0 ? row.configInsuranceMinPd : '',
				maxPd: row.configInsuranceMaxPd > 0 ? row.configInsuranceMaxPd : '',
				lors: new Set(),
			})
		}
		if (row.lengthOfRental != null) groups.get(key).lors.add(Number(row.lengthOfRental))
	}
	const rows = []
	for (const { lors, ...entry } of groups.values()) {
		const nums = [...lors].sort((a, b) => a - b)
		rows.push({ ...entry, lorRange: lorLabel(lors), _minLor: nums[0] ?? 0 })
	}
	return rows.sort((a, b) => a._minLor - b._minLor)
}

const INS_CONFIG_COLUMNS = [
	{ name: 'lorRange', label: 'Duration range', options: { customBodyRowRender: v => c(v) } },
	{ name: 'deltaEurPd', label: 'Delta (€/day)', options: { customBodyRowRender: v => c(v) } },
	{ name: 'minPd', label: 'Min profit/day', options: { customBodyRowRender: v => c(v) } },
	{ name: 'maxPd', label: 'Max profit/day', options: { customBodyRowRender: v => c(v) } },
]

export default function AutoEuropeNotificationsPage() {
	const [activeTab, setActiveTab] = useState(0)
	const [info, setInfo] = useState(null)
	const [revZeroOnly, setRevZeroOnly] = useState(false)
	const { getCheckers, getTableData } = useAutoEuropeNotificationsTable()

	const [searchParams] = useSearchParams()

	// Cascading selections: pricing tool > report > execution > office.
	// Prefilled from query params when arriving from the Checkers page.
	const [selectedPricingTool, setSelectedPricingTool] = useState(() => coerceParam(searchParams.get('toolId')))
	const [selectedReport, setSelectedReport] = useState(() => coerceParam(searchParams.get('idReport')))
	const [selectedExecution, setSelectedExecution] = useState(() => coerceParam(searchParams.get('idReportExecution')))
	const [selectedOffice, setSelectedOffice] = useState(() => coerceParam(searchParams.get('idOfficePickup')))

	useEffect(() => {
		getCheckers()
		// When linked from Checkers with a full selection, fire the list straight away.
		const idPricingTool = searchParams.get('toolId')
		const idReportExecution = searchParams.get('idReportExecution')
		const idOfficePickup = searchParams.get('idOfficePickup')
		if (idPricingTool && idReportExecution && idOfficePickup) {
			getTableData({ idPricingTool, idReportExecution, idOfficePickup })
		}
	}, [])

	// Derive each level's options from the previous selection.
	const checkers = autoEuropeCheckersData.value
	const pricingToolOption = checkers.find(c => c.value === selectedPricingTool)
	const reportOptions = pricingToolOption?.reports ?? []
	const reportOption = reportOptions.find(r => r.value === selectedReport)
	const executionOptions = reportOption?.executions ?? []
	const executionOption = executionOptions.find(e => e.value === selectedExecution)
	const officeOptions = executionOption?.offices ?? []

	const handlePricingToolChange = value => {
		setSelectedPricingTool(value)
		setSelectedReport(null)
		setSelectedExecution(null)
		setSelectedOffice(null)
		setActiveTab(0)
		autoEuropeNotificationsListData.value = []
	}

	const handleReportChange = value => {
		setSelectedReport(value)
		setSelectedExecution(null)
		setSelectedOffice(null)
		setActiveTab(0)
		autoEuropeNotificationsListData.value = []
	}

	const handleExecutionChange = value => {
		setSelectedExecution(value)
		// If the currently selected office also exists in the new execution, keep it and
		// reload straight away (compare the same office across executions).
		const newOffices = executionOptions.find(e => e.value === value)?.offices ?? []
		if (selectedOffice != null && newOffices.some(o => o.value === selectedOffice)) {
			getTableData({
				idPricingTool: selectedPricingTool,
				idReportExecution: value,
				idOfficePickup: selectedOffice,
			})
			return
		}
		setSelectedOffice(null)
		autoEuropeNotificationsListData.value = []
	}

	const handleOfficeChange = value => {
		setSelectedOffice(value)
		setActiveTab(0)
		if (!value) {
			autoEuropeNotificationsListData.value = []
			return
		}
		getTableData({
			idPricingTool: selectedPricingTool,
			idReportExecution: selectedExecution,
			idOfficePickup: value,
		})
	}

	const data = autoEuropeNotificationsListData.value
	const hasInsurance = data.some(r => r.configInsuranceDelta > 0)
	const xlsxColumns = buildXlsxColumns(setInfo, hasInsurance)

	return <PageWrapper
		head={`PS Rentacar | ${AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}`}
		title={AUTO_EUROPE_TOOL_ROUTES.NOTIFICATIONS_TITLE}
		subtitle={AUTO_EUROPE_TOOL_ROUTES.NOTIFICATIONS_SUBTITLE}
	>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{autoEuropeCheckersIsLoading.value
					? <Skeleton variant="rectangular" height={56}/>
					: <Grid container spacing={2}>
						<Grid item xs={12} sm={6} md={3}>
							<AutocompleteSeeker
								label="Pricing Tool"
								placeholder="Select a pricing tool"
								options={checkers}
								value={selectedPricingTool}
								onChange={e => handlePricingToolChange(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<AutocompleteSeeker
								label="Report"
								placeholder="Select a report"
								options={reportOptions}
								value={selectedReport}
								disabled={!selectedPricingTool}
								onChange={e => handleReportChange(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<AutocompleteSeeker
								label="Execution"
								placeholder="Select an execution"
								options={executionOptions}
								value={selectedExecution}
								disabled={!selectedReport}
								onChange={e => handleExecutionChange(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<AutocompleteSeeker
								label="Office"
								placeholder="Select an office"
								options={officeOptions}
								value={selectedOffice}
								disabled={!selectedExecution}
								onChange={e => handleOfficeChange(e.target.value)}
							/>
						</Grid>
					</Grid>
				}
			</Grid>

			<Grid item xs={12}>
				{!selectedOffice
					? <Alert severity="info">Select a pricing tool, report, execution and office to load the notifications.</Alert>
					: <>
						<Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
							<Tab label="Rates"/>
							<Tab label="Data"/>
							<Tab label="Config by provider"/>
							{hasInsurance && <Tab label="Insurance config"/>}
						</Tabs>
						{autoEuropeNotificationsIsLoading.value
							? <Skeleton variant="rectangular" height={400}/>
							: activeTab === 3 && hasInsurance
								? (() => {
									const configRows = buildInsuranceConfigRows(data)
									return <AppCrudTable title="Insurance config" data={configRows} columns={INS_CONFIG_COLUMNS}/>
								})()
								: activeTab === 2
									? (() => {
										const providerRows = buildProviderRows(data)
										return <AppCrudTable
											title="Config by provider"
											data={providerRows}
											columns={buildProviderColumns(providerRows)}
										/>
									})()
									: <AppCrudTable
										title={activeTab === 0 ? 'Rates' : 'Data'}
										data={revZeroOnly
											? data.filter(r => r.rev != null && Number(r.rev) === 0)
											: data}
										columns={activeTab === 0 ? xlsxColumns : CSV_COLUMNS}
										options={{
											customToolbar: () => (
												<Chip
													label="REV = 0"
													size="small"
													color={revZeroOnly ? 'primary' : 'default'}
													variant={revZeroOnly ? 'filled' : 'outlined'}
													onClick={() => setRevZeroOnly(v => !v)}
													sx={{ ml: 1 }}
												/>
											),
										}}
									/>
						}
					</>
				}
			</Grid>
		</Grid>

		<Dialog open={!!info} onClose={() => setInfo(null)} maxWidth="xs" fullWidth>
			<DialogTitle>{info?.title}</DialogTitle>
			<DialogContent dividers>
				{info?.formula && <Box sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 13, mb: info?.fields?.length ? 2 : 0 }}>{info.formula}</Box>}
				{info?.fields?.map(([label, value]) => <Box
					key={label}
					sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: .5 }}
				>
					<Typography variant="body2" color="text.secondary">{label}</Typography>
					<Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value}</Typography>
				</Box>)}
				{info?.result != null && info.result !== '' && <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pt: 1, mt: 1, borderTop: 1, borderColor: 'divider' }}>
					<Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>Result</Typography>
					<Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>{info.result}</Typography>
				</Box>}
			</DialogContent>
		</Dialog>
	</PageWrapper>
}